/**
 * Endereços dos arquivos do chat no layout do CDN do Discord (servidos pela
 * API em `/api/chat/anexos/`). `filesUrl` é essa raiz.
 */

export function fileUrl(filesUrl: string, path: string): string {
  return `${filesUrl.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
}

/** Lados das miniaturas da API (`?size`), com o mesmo arredondamento de lá. */
export const THUMBNAIL_SIZES = [64, 128, 256, 512, 1024, 2048] as const;

/**
 * A imagem do chat no tamanho em que aparece (`pixels` = maior lado, já com
 * a densidade da tela): a API manda a miniatura em WebP, e a imagem já
 * menor, animada ou que não é imagem vem como está. Endereço de fora do
 * chat (embed de outro site) não muda.
 */
export function sizedImageUrl(url: string, pixels: number): string {
  if (!url || !(pixels > 0) || !url.includes('/chat/anexos/')) return url;
  if (/[?&]size=/.test(url)) return url;
  const size =
    THUMBNAIL_SIZES.find((side) => side >= pixels) ??
    THUMBNAIL_SIZES[THUMBNAIL_SIZES.length - 1];
  return `${url}${url.includes('?') ? '&' : '?'}size=${size}`;
}

/** Avatar (`avatars/<pessoa>/<hash>.png|gif`); nulo = a interface desenha as iniciais. */
export function avatarUrl(
  filesUrl: string,
  user: { id: string; avatar: string | null },
  animated = false,
): string | null {
  if (!user.avatar) return null;
  const ext = animated && user.avatar.startsWith('a_') ? 'gif' : 'png';
  return fileUrl(filesUrl, `avatars/${user.id}/${user.avatar}.${ext}`);
}

export function guildIconUrl(
  filesUrl: string,
  guild: { id: string; icon: string | null },
): string | null {
  if (!guild.icon) return null;
  return fileUrl(filesUrl, `icons/${guild.id}/${guild.icon}.png`);
}

export function emojiUrl(
  filesUrl: string,
  emoji: { id: string; animated?: boolean },
): string {
  return fileUrl(
    filesUrl,
    `emojis/${emoji.id}.${emoji.animated ? 'gif' : 'png'}`,
  );
}

/** Figurinha: 1 e 2 PNG, 3 Lottie (JSON), 4 GIF. */
export function stickerUrl(
  filesUrl: string,
  sticker: { id: string; format_type: number },
): string {
  const ext =
    sticker.format_type === 3
      ? 'json'
      : sticker.format_type === 4
        ? 'gif'
        : 'png';
  return fileUrl(filesUrl, `stickers/${sticker.id}.${ext}`);
}

/** Capa do evento (`guild-events/<evento>/<hash>.png`); nula sem capa. */
export function eventCoverUrl(
  filesUrl: string,
  event: { id: string; image: string | null },
): string | null {
  if (!event.image) return null;
  return fileUrl(filesUrl, `guild-events/${event.id}/${event.image}.png`);
}

/** Ícone do grupo (`channel-icons/<canal>/<hash>.png`); nulo sem ícone. */
export function groupIconUrl(
  filesUrl: string,
  channel: { id: string; icon?: string | null },
): string | null {
  if (!channel.icon) return null;
  return fileUrl(filesUrl, `channel-icons/${channel.id}/${channel.icon}.png`);
}

/** Avatar só no servidor (`guilds/<s>/users/<p>/avatars/<hash>.png`); nulo sem. */
export function memberAvatarUrl(
  filesUrl: string,
  guildId: string,
  member: { user?: { id: string }; avatar?: string | null },
  userId?: string,
): string | null {
  const id = userId ?? member.user?.id;
  if (!member.avatar || !id) return null;
  return fileUrl(
    filesUrl,
    `guilds/${guildId}/users/${id}/avatars/${member.avatar}.png`,
  );
}

/** Faixa do perfil (`banners/<pessoa>/<hash>.png`); nula sem faixa. */
export function bannerUrl(
  filesUrl: string,
  user: { id: string; banner?: string | null },
): string | null {
  if (!user.banner) return null;
  return fileUrl(filesUrl, `banners/${user.id}/${user.banner}.png`);
}
